import { Fragment, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Text, useTheme } from '@ui-kitten/components';

import constants from '../Config/constants';
import MealPlanHelper from '../Helper/MealPlan';
import useAlert, { getConfirmOptions } from '../Hooks/useAlert';
import { mealPlan } from '../RTK/defaultValues';
import { setMealPlanDay } from '../RTK/mealPlan';
import {
  selectIsDayHasCart,
  selectMealPlanDay,
} from '../RTK/mealPlan/selectors';
import ThemeColor from '../Theme/colors';
import ThemeStyle from '../Theme/styles';

import Button from './Button';

type Type = {
  value: string;
};

type Props = {
  small?: boolean;
  value?: string;
  onChange: (data: Type) => void;
};

const types: Type[] = [
  { value: mealPlan.type.threeDay },
  { value: mealPlan.type.fiveDay },
];

const textCategory = constants.isWeb ? 'p2' : 'p1';
const textCategorySmall = constants.isWeb ? 'c1' : 'p2';
const tuesdayAndThursday = [2, 4];

const MealPlanTypes = ({ small, value, onChange }: Props) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const store_theme = useTheme();
  const mealPlanDay = useSelector(selectMealPlanDay);
  const hasCartOnTuesdayOrThursday = useSelector((state) =>
    selectIsDayHasCart(state, tuesdayAndThursday),
  );

  const _onTypeSelection = async (type: Type) => {
    if (type.value === mealPlan.type.threeDay && hasCartOnTuesdayOrThursday) {
      alert(
        'Some Items Will Be Removed',
        'Switching to a 3-day meal plan means any items scheduled for Tuesday or Thursday will be permanently removed from your cart.',
        getConfirmOptions({ onConfirm: () => onChange(type) }),
      );
    } else {
      if (type.value === mealPlan.type.fiveDay && mealPlanDay.value > 1) {
        // if selecting 5 day plan AND meal plan selected day is not monday
        dispatch(setMealPlanDay(MealPlanHelper.getDayValue(5, 2))); // set the selected day to tuesday
      }
      onChange(type);
    }
  };

  return (
    <View style={[ThemeStyle.flexDirectionRow, small && ThemeStyle.flex1]}>
      {types.map((type: Type, index: number) => {
        const isActive = value === type.value;
        const style = constants.isWeb
          ? ({ hovered }) => [
              styles.contentContainer,
              small && ThemeStyle.flex1,
              hovered && {
                backgroundColor:
                  `${store_theme?.button_color}1A` || ThemeColor.green100,
              },
              isActive && {
                backgroundColor: store_theme?.button_color || ThemeColor.green,
              },
            ]
          : small && ThemeStyle.flex1;
        return (
          <Fragment key={index}>
            <Button
              onPress={() => _onTypeSelection(type)}
              style={style}
              isHoverable={constants.isWeb}
              plain
            >
              <View
                style={[
                  !constants.isWeb && styles.contentContainer, // mobile style only
                  !constants.isWeb && isActive && styles.active, // mobile style only
                  small ? styles.containerSmall : styles.container,
                ]}
              >
                <View style={ThemeStyle.flexDirectionRowCenter}>
                  <Icon
                    name="calendar-outline"
                    fill={
                      isActive
                        ? store_theme?.button_text_color || ThemeColor.white
                        : store_theme?.icon_color || ThemeColor.green
                    }
                    style={small ? styles.iconSmall : styles.icon}
                  />
                  <Text
                    appearance={isActive ? 'alternative' : 'default'}
                    category={small ? textCategorySmall : textCategory}
                    style={ThemeStyle.capitalize}
                  >
                    {type.value.replace('_', ' ')}
                  </Text>
                </View>
                <Text
                  appearance={isActive ? 'alternative' : 'default'}
                  category={small ? textCategorySmall : textCategory}
                >
                  Meal Plan
                </Text>
              </View>
            </Button>

            {index !== types.length - 1 && (
              <View
                style={
                  small
                    ? ThemeStyle.rowSeparator
                    : ThemeStyle.pageHorizontalSpacing
                }
              />
            )}
          </Fragment>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: 'center',
    backgroundColor: ThemeColor.lightGray,
    borderRadius: 10,
  },
  container: {
    ...ThemeStyle.pageHorizontalSpacing,
    ...ThemeStyle.pageVerticalSpacing,
  },
  containerSmall: {
    ...ThemeStyle.pageHorizontalSpacingSmall,
    ...ThemeStyle.pageVerticalSpacingMedium,
  },
  active: {
    backgroundColor: ThemeColor.green,
  },
  icon: {
    ...ThemeStyle.spacingRightMedium,
    width: 20,
    height: 20,
  },
  iconSmall: {
    ...ThemeStyle.spacingRightSmall,
    width: 15,
    height: 15,
  },
});

export default MealPlanTypes;
